/**
 * Ideas for you — the short list under the companion's composer at the START
 * of a conversation (ui-ux-guideline §2.9 item 2; #2265 made it the only
 * suggestion surface, replacing the per-turn reply chips).
 *
 * The generated suggestions (`POST /chat/suggestions`, in the reader's
 * language) and the static chips that stand in until they land
 * (`buildParentIdeas`). Both hold the memorial rule themselves: the query is
 * off and `buildChips` takes the gate. While the family record is in flight
 * the gate is shut, because the fail-safe mode is 'active' and treatment copy
 * must not flash before the record lands (#644/#693).
 *
 * Moved out of `DashboardPage` (#2265) so `/chat` offers the same list on a
 * new thread. Every read shares its cache key with the page that already makes
 * it (`tasks`, `medical/appointments`, `medications`, `family-mine`), so a
 * host that fetches them anyway pays nothing extra.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useCompanionName } from '@/hooks/useCompanionName';
import { useMsg } from '@/i18n/msg';
import { api } from '@/services/api';
import { chatApi } from '@/services/chatApi';
import { getTimeOfDay } from '@/lib/utils';
import { resolveDashboardMode } from '@/lib/dashboard-mode';
import { visibleParentTasks } from '@/lib/task-display';
import { buildChips } from '@/lib/parent-home-greeting';
import { buildParentIdeas, type ParentIdea } from '@/lib/parent-home-ideas';

export interface CompanionIdeasData {
  ideas: ParentIdea[];
  /** Memorial, or the family record still in flight: no ideas render (#644). */
  memorialGate: boolean;
}

export function useCompanionIdeas(): CompanionIdeasData {
  const { user } = useAuth();
  const isParent = user?.role === 'parent';
  const companionName = useCompanionName();
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;
  const resolve = useMsg();

  const { data: tasksData } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => api.tasks.getAll(),
    enabled: isParent,
  });
  const { data: appointmentsData } = useQuery({
    queryKey: ['medical', 'appointments'],
    queryFn: () => api.medical.appointments.getAll(),
    enabled: isParent,
  });
  const { data: medicationsData } = useQuery({
    queryKey: ['medications'],
    queryFn: () => api.medical.medications.getAll(),
    enabled: isParent,
  });
  const { data: familyData, isLoading: familyLoading } = useQuery({
    queryKey: ['family-mine'],
    queryFn: () => api.families.getMine(),
    enabled: isParent,
  });

  const family = familyData?.data ?? null;
  const mode = resolveDashboardMode(family?.patient?.treatment_phase);
  const memorialGate = familyLoading || mode === 'memorial';

  // The one task filter every parent surface uses (#693).
  const openTaskCount = useMemo(
    () => (familyLoading ? [] : visibleParentTasks(tasksData?.data || [], mode))
      .filter((t) => t.status === 'open').length,
    [tasksData, mode, familyLoading],
  );
  // Appointments and medications are treatment records WHOLE — withheld
  // entirely under the gate.
  const medications = useMemo(
    () => (memorialGate ? [] : medicationsData?.data || []),
    [medicationsData, memorialGate],
  );
  const nextAppt = useMemo(() => {
    if (memorialGate) return undefined;
    const now = Date.now();
    return (appointmentsData?.data || [])
      .filter((a) => new Date(a.date_time).getTime() >= now)
      .sort((a, b) => new Date(a.date_time).getTime() - new Date(b.date_time).getTime())[0];
  }, [appointmentsData, memorialGate]);

  const firstName = user?.profile?.first_name ?? '';
  const childFirstName = (family?.patient?.name ?? '').trim().split(/\s+/)[0] ?? '';
  const hasActiveMeds = medications.some((m) => m.active);
  const nextApptInDays = nextAppt
    ? Math.max(0, Math.ceil((new Date(nextAppt.date_time).getTime() - Date.now()) / 86400000))
    : null;

  const { data: suggestions } = useQuery({
    queryKey: [
      'maya-suggestions',
      // Generated in the reader's language (R3-B13), so a language switch is
      // a different query.
      language,
      getTimeOfDay(),
      companionName,
      firstName,
      childFirstName,
      openTaskCount,
      Boolean(nextAppt),
      nextApptInDays,
      hasActiveMeds,
    ],
    queryFn: () =>
      chatApi.getMayaSuggestions({
        time_of_day: getTimeOfDay(),
        companion_name: companionName,
        parent_first_name: firstName,
        child_first_name: childFirstName,
        open_tasks: openTaskCount,
        // helpers is pinned to 0 — the helper role is gated off and being removed
        // platform-wide (2026-06-04); the suggestions API still expects the field.
        helpers: 0,
        has_next_appt: Boolean(nextAppt),
        next_appt_in_days: nextApptInDays,
        has_active_meds: hasActiveMeds,
      }),
    enabled: isParent && Boolean(childFirstName) && !memorialGate,
    staleTime: 60_000,
    retry: 1,
  });

  // The static chips are descriptors; each resolved label is also the message
  // the idea sends, in the UI language (R3-B2).
  const ideas = useMemo(
    () =>
      buildParentIdeas({
        suggestions,
        fallback: buildChips(nextAppt, medications, openTaskCount, companionName, memorialGate).map((c) => ({
          label: resolve(c.label),
          href: c.href,
        })),
      }),
    [suggestions, nextAppt, medications, openTaskCount, companionName, memorialGate, resolve],
  );

  return { ideas, memorialGate };
}
